import * as React from 'react';
import { Row, Col } from 'react-bootstrap';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose } from 'redux';

import { withTaskRecovery } from 'af-root/context/taskRecoveryContext';
import { useTaskRecovery } from 'af-root/hooks/useTaskRecovery';

import type TimeAllocationEntryVM from 'ab-viewModels/timeAllocationEntry/timeAllocationEntry';
import type WorkOrderFieldReportCardVM from 'ab-viewModels/workOrder/workOrderFieldReportCard.viewModel';
import type AssociatedSubjobVM from 'ab-viewModels/workRequest/associatedSubjob.viewModel';

import { isAllowed } from '@acceligentllc/shared/utils/auth';
import { formatDate } from '@acceligentllc/shared/utils/time';

import { FieldReportAccessRole } from '@acceligentllc/shared/enums/fieldReportAccessRole';
import type { CalculatedReportDisplayStatus } from '@acceligentllc/shared/enums/reportDisplayStatus';
import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';
import { WorkOrderReviewStatus, isReviewable, isApprovable, isRejectable, isFinalizeable } from '@acceligentllc/shared/enums/workOrderReviewStatus';

import type { RootState } from 'af-reducers';

import { bemElement } from 'af-utils/bem.util';
import { useToggle } from 'af-utils/react.util';
import socket from 'af-utils/socket.util';

import PagePermissions from 'ab-enums/pagePermissions.enum';
import SocketEvent from 'ab-enums/socketEvent.enum';

import { Button, infoToast } from '@acceligentllc/storybook';

import Dropdown from 'af-components/Controls/Dropdown';
import LabelWithColor from 'af-components/LabelWithColor';
import LockedValue from 'af-components/LockedValue';
import ProjectSubJobIndicator from 'af-components/ProjectSubJobIndicator';

import * as JobWorkSummaryActions from 'af-actions/jobWorkSummary';
import * as WorkOrderActions from 'af-actions/workOrder';

import AccessModal from '../FieldReportAccess';
import styles from './styles.module.scss';
import WorkOrderAssociatedSubJobData from './WorkOrderAssociatedSubJobData';
import WorkOrderLinks from './WorkOrderLinks';
import WorkOrderPauseReason from './WorkOrderPauseReason';
import WorkOrderRejectionReason from './WorkOrderRejectionReason';
import WorkOrderReviewedByLabel from './WorkOrderReviewedByLabel';
import DisplayReviewStatusLabel from '../../Shared/DisplayReviewStatusLabel';
import VirtualReportExportModal from '../../WorkOrdersList/FullTable/VirtualReportExportModal';

const ALLOWED_RETRIES = 4;

// Function to compare time allocation entries
const areEntriesDifferent = (prevEntries: TimeAllocationEntryVM[], newEntries: TimeAllocationEntryVM[]) => {
	if (prevEntries.length !== newEntries.length) {
		return true;
	}

	for (let i = 0; i < prevEntries.length; i++) {
		const prevEntry = prevEntries[i];
		const newEntry = newEntries[i];
		if (
			prevEntry.id !== newEntry.id ||
			prevEntry.accountId !== newEntry.accountId ||
			prevEntry.workOrderId !== newEntry.workOrderId ||
			prevEntry.workType !== newEntry.workType ||
			prevEntry.time !== newEntry.time ||
			prevEntry.allocatedWorkRequestId !== newEntry.allocatedWorkRequestId
		) {
			return true;
		}
	}

	return false;
};

interface DropdownOption {
	label: string;
	onClick: () => void;
}

interface OwnProps {
	workOrder: WorkOrderFieldReportCardVM;
	fieldReportAccessDayDuration: number;
	isManagerAdminSI: boolean;
	calculatedReportDisplayStatus: Nullable<CalculatedReportDisplayStatus>;
	canApproveOrReject: boolean;
	canCreate: boolean;
	canSubmit: boolean;
	canFinalize: boolean;
	isCanceled: boolean;
	isFRStatusOfInterest: boolean;
	isReadOnly: boolean;
	createFieldReport: () => void;
	submitForReview: () => void;
	approve: () => void;
	reject: () => void;
	finalize: () => void;
	orgAlias: string;
	companyName: string;
	timeAllocationEntries: TimeAllocationEntryVM[];
	jobId: number;
	redirectToVirtualFieldReport: (subjob: AssociatedSubjobVM) => void;
	associatedWRId: Nullable<number>;
	setShowedAssociatedSubjob: (subjob: Nullable<AssociatedSubjobVM>) => void;
	redirectToFieldReport: () => void;
	redirectToWorkOrder: () => void;
	redirectToJob: (jobId: number) => void;
	reviewVirtual: () => void;
	virtualReportReviewedAt: Nullable<Date>;
	virtualReportLastReviewedBy: Nullable<string>;
	showedAssociatedSubjob: Nullable<AssociatedSubjobVM>;
	isRefreshing: boolean;
}

type Props = OwnProps & ConnectedProps<typeof connector>;

const WorkOrderInfoCard = (props: Props) => {
	const {
		workOrder,
		fieldReportAccessDayDuration,
		isCanceled,
		calculatedReportDisplayStatus,
		isFRStatusOfInterest,
		canApproveOrReject,
		canFinalize,
		canSubmit,
		isManagerAdminSI,
		isReadOnly,
		canCreate,
		createFieldReport,
		downloadCompleteReportsPdf,
		downloadPublicReportsPdf,
		downloadCompleteVirtualReportPDF,
		downloadCustomerVirtualReportPDF,
		findOrCreateCompleteReportBulkGroupCode,
		findOrCreatePublicReportLink,
		findOrCreateCompleteVirtualReportGroupCode,
		findOrCreatePublicVirtualReportGroupCode,
		submitForReview,
		approve,
		reject,
		finalize,
		findAssociatedSubjobs,
		timeAllocationEntries,
		setShowedAssociatedSubjob,
		associatedWRId,
		redirectToJob,
		redirectToFieldReport,
		redirectToVirtualFieldReport,
		redirectToWorkOrder,
		reviewVirtual,
		virtualReportReviewedAt,
		virtualReportLastReviewedBy,
		canSeeAssociatedSubJobData,
		canSeeRejectionReason,
		showedAssociatedSubjob,
		isRefreshing,
		canViewForeman,
	} = props;

	const {
		value: showAccessModal,
		setToTrue: openAccessModal,
		setToFalse: closeAccessModal,
	} = useToggle(false);

	const [allocations, setAllocations] = React.useState<Nullable<AssociatedSubjobVM[]>>(null);
	const [showExportCompleteVirtualReportModal, setShowExportCompleteVirtualReportModal] = React.useState(false);
	const [showExportPublicVirtualReportModal, setShowExportPublicVirtualReportModal] = React.useState(false);

	const prevTimeAllocationEntriesRef = React.useRef(timeAllocationEntries);

	const isShowingVirtualReport = React.useMemo(() => !!associatedWRId, [associatedWRId]);

	const showWorkOrderAssociatedSubJobData = React.useMemo(() => {
		return !isShowingVirtualReport
			&& workOrder.isProjectOrSubjob
			&& canSeeAssociatedSubJobData
			&& process.env.FTD_PROJECT !== 'true';
	}, [
		isShowingVirtualReport,
		workOrder.isProjectOrSubjob,
		canSeeAssociatedSubJobData,
	]);

	const fetchAssociatedSubJobs = React.useCallback(async () => {
		if (!workOrder.isProjectOrSubjob || !canSeeAssociatedSubJobData) {
			return;
		}
		const _allocations = await findAssociatedSubjobs(workOrder.id);

		setAllocations(_allocations);
	}, [findAssociatedSubjobs, workOrder.id, workOrder.isProjectOrSubjob, canSeeAssociatedSubJobData]);

	React.useEffect(() => {
		fetchAssociatedSubJobs();
	}, [fetchAssociatedSubJobs]);

	React.useEffect(() => {
		if (!workOrder.isProjectOrSubjob || !canSeeAssociatedSubJobData) {
			return;
		}
		socket.connection?.subscribe(SocketEvent.V2.BE.FIELD_REPORT_LIST.ASSOCIATED_SUB_JOB_DATA_UPDATED, fetchAssociatedSubJobs);

		return () => {
			if (!workOrder.isProjectOrSubjob) {
				return;
			}
			socket.connection?.unsubscribe(SocketEvent.V2.BE.FIELD_REPORT_LIST.ASSOCIATED_SUB_JOB_DATA_UPDATED);
		};
	}, [fetchAssociatedSubJobs, workOrder.isProjectOrSubjob, canSeeAssociatedSubJobData]);

	React.useEffect(() => {
		if (allocations) {
			setShowedAssociatedSubjob(allocations.find((all) => all.id === associatedWRId)!);
		}
	}, [allocations, associatedWRId, setShowedAssociatedSubjob]);

	React.useEffect(() => {
		if (areEntriesDifferent(prevTimeAllocationEntriesRef.current, timeAllocationEntries)) {
			fetchAssociatedSubJobs();
		}
		// Update the ref to the current entries after comparison
		prevTimeAllocationEntriesRef.current = timeAllocationEntries;
	}, [fetchAssociatedSubJobs, timeAllocationEntries]);

	const createReport = React.useCallback(() => {
		createFieldReport();
	}, [createFieldReport]);

	const taskRecovery = useTaskRecovery();

	const exportToCustomerPDF = React.useCallback(async () => {
		infoToast(`${workOrder.id}-customer`, { title: 'Started preparing customer report for download...' });
		const publicLink = await findOrCreatePublicReportLink([workOrder.id]);
		if (!!publicLink) {
			await downloadPublicReportsPdf(publicLink, `${workOrder.code}_report`, taskRecovery, ALLOWED_RETRIES);
		}
	}, [findOrCreatePublicReportLink, workOrder.id, workOrder.code, downloadPublicReportsPdf, taskRecovery]);

	const exportToCompletePDF = React.useCallback(async () => {
		infoToast(`${workOrder.id}-complete`, { title: 'Started preparing complete report for download...' });
		const bulkGroupCode = await findOrCreateCompleteReportBulkGroupCode([workOrder.id]);
		if (!!bulkGroupCode) {
			await downloadCompleteReportsPdf(bulkGroupCode, `${workOrder.code}_report`, taskRecovery, ALLOWED_RETRIES);
		}
	}, [findOrCreateCompleteReportBulkGroupCode, workOrder.id, workOrder.code, downloadCompleteReportsPdf, taskRecovery]);

	const exportCompleteVirtualFieldReport = React.useCallback(async (workRequestId: number, workRequestName: string) => {
		infoToast(`${workRequestId}-${workRequestName}-virtual`, { title: 'Started preparing report for download...' });

		const publicLink = await findOrCreateCompleteVirtualReportGroupCode(workOrder.id, workRequestId);
		if (!!publicLink) {
			await downloadCompleteVirtualReportPDF(publicLink, workRequestId, `${workOrder.code}_VR_${workRequestName}`, taskRecovery, ALLOWED_RETRIES);
		}
	}, [findOrCreateCompleteVirtualReportGroupCode, workOrder.id, workOrder.code, downloadCompleteVirtualReportPDF, taskRecovery]);

	const exportPublicVirtualFieldReport = React.useCallback(async (workRequestId: number, workRequestName: string) => {
		infoToast(`${workRequestId}-${workRequestName}-virtual`, { title: 'Started preparing report for download...' });

		const publicLink = await findOrCreatePublicVirtualReportGroupCode(workOrder.id, workRequestId);
		if (!!publicLink) {
			await downloadCustomerVirtualReportPDF(publicLink, workRequestId, `${workOrder.code}_VR_${workRequestName}`, taskRecovery, ALLOWED_RETRIES);
		}
	}, [findOrCreatePublicVirtualReportGroupCode, workOrder.id, workOrder.code, downloadCustomerVirtualReportPDF, taskRecovery]);

	const openExportCompleteVirtualReportModal = React.useCallback(() => {
		setShowExportCompleteVirtualReportModal(true);
	}, []);

	const closeCompleteVirtualReportExportModal = React.useCallback(() => {
		setShowExportCompleteVirtualReportModal(false);
	}, []);

	const openExportPublicVirtualReportModal = React.useCallback(() => {
		setShowExportPublicVirtualReportModal(true);
	}, []);

	const closePublicVirtualReportExportModal = React.useCallback(() => {
		setShowExportPublicVirtualReportModal(false);
	}, []);

	const submitTitle = React.useMemo(() => {
		return isManagerAdminSI && workOrder.reviewStatus === WorkOrderReviewStatus.DRAFT ? 'Submit' : 'Submit for Review';
	}, [isManagerAdminSI, workOrder.reviewStatus]);

	const actionOptions = React.useMemo(() => {

		const options: DropdownOption[] = [];

		if (isManagerAdminSI && !isShowingVirtualReport) {
			options.push({ label: 'Manage Access', onClick: openAccessModal });
		}

		if (!isReadOnly && canCreate && !isCanceled && !isShowingVirtualReport) {
			options.push({ label: 'New Field Report', onClick: createReport });
		}

		if (!isCanceled && !isShowingVirtualReport) {
			options.push({ label: 'Export for Customer', onClick: exportToCustomerPDF });
			options.push({ label: 'Export Complete', onClick: exportToCompletePDF });
		}

		if (showWorkOrderAssociatedSubJobData && allocations?.length && !isShowingVirtualReport) {
			options.push({ label: 'Export Complete Associated Subjob', onClick: openExportCompleteVirtualReportModal });
			options.push({ label: 'Export Associated Subjob for Customer', onClick: openExportPublicVirtualReportModal });
		}

		if (showedAssociatedSubjob) {
			options.push({ label: `Export Complete for ${showedAssociatedSubjob.jobCode}`, onClick: () => exportCompleteVirtualFieldReport(showedAssociatedSubjob.id, showedAssociatedSubjob.jobCode) });
			options.push({ label: `Export ${showedAssociatedSubjob.jobCode} for Customer`, onClick: () => exportPublicVirtualFieldReport(showedAssociatedSubjob.id, showedAssociatedSubjob.jobCode) });
		}

		return options;
	}, [
		allocations,
		canCreate,
		createReport,
		exportToCompletePDF,
		exportToCustomerPDF,
		isCanceled,
		isManagerAdminSI,
		isReadOnly,
		openAccessModal,
		showWorkOrderAssociatedSubJobData,
		exportCompleteVirtualFieldReport,
		isShowingVirtualReport,
		showedAssociatedSubjob,
		openExportCompleteVirtualReportModal,
		openExportPublicVirtualReportModal,
		exportPublicVirtualFieldReport,
	]);

	const renderOption = React.useCallback((option: DropdownOption) => {
		return <div className={styles['dropdown-option']}>{option.label}</div>;
	}, []);

	const renderDropdownOptionsButton = () => {

		if (!actionOptions.length) {
			return null;
		}

		return (
			<div className={bemElement('field-report-work-order-info-card', 'dropdown-options')}>
				<Dropdown<DropdownOption>
					containerClassName="field-report-list-table__action-dropdown"
					id="actions"
					isActionDropdown={true}
					labelKey="label"
					options={actionOptions}
					renderMenuItem={renderOption}
				/>
			</div>
		);
	};

	const renderButtonsAndActions = () => {
		const isFinalizeDisabled = !isFinalizeable(workOrder.reviewStatus, workOrder.reviewLevel)
			|| !canFinalize || isCanceled || workOrder.isPaused || isShowingVirtualReport || isRefreshing;
		const isSubmitForReviewDisabled = !isReviewable(workOrder.reviewStatus, workOrder.reviewLevel)
			|| !canSubmit || isCanceled || workOrder.isPaused || isShowingVirtualReport || isRefreshing;
		const isApprovalDisabled = !isApprovable(workOrder.reviewStatus)
			|| !canApproveOrReject || isCanceled || workOrder.isPaused || isShowingVirtualReport || isRefreshing;
		const isRejectDisabled = !isRejectable(workOrder.reviewStatus, workOrder.reviewLevel)
			|| !canApproveOrReject || isCanceled || workOrder.isPaused || isShowingVirtualReport || isRefreshing;
		const showReviewButton =
			isShowingVirtualReport
			&& (!virtualReportReviewedAt || virtualReportReviewedAt < workOrder.reportLastUpdatedAt!)
			&& !isRefreshing;
		const hasReviewInfo = !!virtualReportReviewedAt;
		const isReviewOutdated = hasReviewInfo && virtualReportReviewedAt < workOrder.reportLastUpdatedAt!;

		return (
			<div className="field-report-work-order-info-card__header__actions">
				{hasReviewInfo &&
					<div className="field-report-work-order-info-card__header__virtual_review_info">
						<div className="field-report-work-order-info-card__header__virtual_reviewed_by">
							Last reviewed {formatDate(virtualReportReviewedAt, TimeFormat.FULL_DATE)}
						</div>
						By {virtualReportLastReviewedBy}
						{isReviewOutdated && <div className="field-report-work-order-info-card__header__virtual_review_outdated">
							Changes have been made since last review
						</div>}
					</div>
				}
				{
					showReviewButton &&
					<Button
						customStyleClass="field-report-work-order-info-card__header__virtual_review_button"
						icon="ReviewVisilibity"
						label="Review"
						onClick={reviewVirtual}
						style="secondary"
					/>
				}
				{
					isRefreshing &&
					<>
						<Button
							disabled={isApprovalDisabled}
							label="Approve"
							onClick={approve}
							style="primary"
							submitting={true}
						/>
						<Button
							disabled={isRejectDisabled}
							label="Reject"
							onClick={reject}
							style="danger"
							submitting={true}
						/>
					</>
				}
				{!isSubmitForReviewDisabled &&
					<Button
						disabled={isSubmitForReviewDisabled}
						label={submitTitle}
						onClick={submitForReview}
						style="primary"
					/>
				}
				{!isApprovalDisabled &&
					<Button
						disabled={isApprovalDisabled}
						label="Approve"
						onClick={approve}
						style="primary"
					/>
				}
				{!isRejectDisabled &&
					<Button
						disabled={isRejectDisabled}
						label="Reject"
						onClick={reject}
						style="danger"
					/>
				}
				{!isFinalizeDisabled &&
					<Button
						disabled={isFinalizeDisabled}
						label="Finalize"
						onClick={finalize}
						style="primary"
					/>
				}
				{renderDropdownOptionsButton()}
			</div>
		);
	};

	return (
		<div className="form-box field-report-work-order-info-card">
			<div className="field-report-work-order-info-card__header">
				<div className="field-report-work-order-info-card__header__title">
					<DisplayReviewStatusLabel
						calculatedReportDisplayStatus={calculatedReportDisplayStatus}
						isCondensedView={false}
						isOfInterest={isFRStatusOfInterest}
					/>
					{isCanceled &&
						<div className="field-report-work-order-info-card__header__title__canceled">
							CANCELED
						</div>
					}
					<div className="field-report-work-order-info-card__header__title__name">
						{workOrder.code}
					</div>
					<div className="field-report-work-order-info-card__header__title__project-indicator">
						<ProjectSubJobIndicator isProject={workOrder.isProjectMainJob} isSubJob={workOrder.isProjectSubJob} />
					</div>
				</div>
				{renderButtonsAndActions()}
			</div>
			<Row className="row--padded">
				<Col sm={3}>
					<LockedValue
						defaultValue="N/A"
						label="Date of Work"
						value={formatDate(workOrder.dueDate, TimeFormat.SB_DATE_SHORT, TimeFormat.DB_DATE_ONLY)}
					/>
				</Col>
				<Col sm={3}>
					<LockedValue
						defaultValue="N/A"
						label="Job Title"
						value={workOrder.jobTitle}
					/>
				</Col>
				<Col sm={3}>
					<LockedValue
						defaultValue="N/A"
						label="Job Location"
						value={workOrder.workLocationAddress}
					/>
				</Col>
				{workOrder.customerWorkOrder &&
					<Col sm={3}>
						<LockedValue
							label="Customer WO"
							value={workOrder.customerWorkOrder}
						/>
					</Col>
				}
				<Col sm={3}>
					<LockedValue
						defaultValue="N/A"
						label="Superintendent"
						value={workOrder.supervisorFullName}
					/>
				</Col>
				<Col sm={3}>
					<LockedValue
						defaultValue="N/A"
						label="Crew Type"
						value={
							workOrder.crewTypeColor
								? <LabelWithColor color={workOrder.crewTypeColor} text={workOrder.crewTypeName} />
								: null
						}
					/>
				</Col>
				{
					canViewForeman && <Col sm={3}>
						<LockedValue
							defaultValue="N/A"
							label="Crew #"
							value={workOrder.crewNumber}
						/>
					</Col>
				}

				<Col sm={3}>
					<LockedValue
						defaultValue="N/A"
						label="Last WO Update"
						value={
							<LockedValue
								inverse={true}
								label={formatDate(workOrder.updatedAt, TimeFormat.FULL_DATE_LONG, TimeFormat.ISO_DATETIME)}
								value={`by ${workOrder.updatedBy.fullNameShort}`}
							/>
						}
					/>
				</Col>
				<Col sm={3}>
					<WorkOrderReviewedByLabel
						currentReviewHistoryItem={workOrder.currentReviewHistoryItem}
						reviewLevel={workOrder.reviewLevel}
						reviewStatus={workOrder.reviewStatus}
					/>
				</Col>
				{
					associatedWRId &&
					<WorkOrderLinks
						jobId={associatedWRId!}
						redirectToFieldReport={redirectToFieldReport}
						redirectToJob={redirectToJob}
						redirectToWorkOrder={redirectToWorkOrder}
					/>
				}
			</Row>
			{
				canSeeRejectionReason &&
				<WorkOrderRejectionReason
					workOrderReviewHistoryItem={workOrder.currentReviewHistoryItem}
				/>
			}
			<WorkOrderPauseReason
				pauseReason={workOrder.pauseReason}
			/>
			{
				showWorkOrderAssociatedSubJobData &&
				<WorkOrderAssociatedSubJobData
					allocations={allocations}
					fetchAssociatedSubJobs={fetchAssociatedSubJobs}
					redirectToVirtualFieldReport={redirectToVirtualFieldReport}
				/>
			}
			<AccessModal
				close={closeAccessModal}
				fieldReportAccessDayDuration={fieldReportAccessDayDuration}
				showModal={showAccessModal}
				workOrderId={workOrder.id}
			/>
			<VirtualReportExportModal
				closeModal={closeCompleteVirtualReportExportModal}
				forCustomer={false}
				onSubmit={exportCompleteVirtualFieldReport}
				showModal={showExportCompleteVirtualReportModal}
				workOrderId={workOrder.id}
			/>
			<VirtualReportExportModal
				closeModal={closePublicVirtualReportExportModal}
				forCustomer={true}
				onSubmit={exportPublicVirtualFieldReport}
				showModal={showExportPublicVirtualReportModal}
				workOrderId={workOrder.id}
			/>
		</div>
	);
};

function mapStateToProps(state: RootState, ownProps: OwnProps) {
	const { user: { companyData, userData } } = state;
	const { isManagerAdminSI } = ownProps;

	if (!userData || !companyData) {
		throw new Error('User not logged in');
	}

	const hasPermissionsToAccessWorkSummary = isAllowed(
		PagePermissions.COMPANY.JOBS.MANAGE_WORK_SUMMARY,
		companyData.permissions,
		companyData.isCompanyAdmin,
		userData.role
	);

	const canViewForeman = isAllowed(
		PagePermissions.COMPANY.FOREMAN.VIEW,
		companyData.permissions,
		companyData.isCompanyAdmin,
		userData.role
	);

	const fieldReportAccessRole = ownProps.workOrder?.fieldReportAccessRole ?? null;
	const isAssignedToWorkOrder = ownProps.workOrder.assignedToIds?.includes(companyData.accountId);
	const showRejectReasonForAccount = fieldReportAccessRole === FieldReportAccessRole.FIELD_WORKER ? isAssignedToWorkOrder : true;

	return {
		canSeeAssociatedSubJobData: isManagerAdminSI || hasPermissionsToAccessWorkSummary,
		canSeeRejectionReason: showRejectReasonForAccount,
		canViewForeman,
	};
}

function mapDispatchToProps() {
	return {
		downloadCompleteReportsPdf: WorkOrderActions.downloadCompleteReportPDF,
		downloadPublicReportsPdf: WorkOrderActions.downloadPublicReportPDF,
		downloadCompleteVirtualReportPDF: WorkOrderActions.downloadCompleteVirtualReportPDF,
		downloadCustomerVirtualReportPDF: WorkOrderActions.downloadCustomerVirtualReportPDF,
		findOrCreateCompleteReportBulkGroupCode: WorkOrderActions.findOrCreateCompleteGroupCode,
		findOrCreatePublicReportLink: WorkOrderActions.findOrCreatePublicGroupCode,
		findOrCreateCompleteVirtualReportGroupCode: WorkOrderActions.findOrCreateCompleteVirtualGroupCode,
		findOrCreatePublicVirtualReportGroupCode: WorkOrderActions.findOrCreatePublicVirtualGroupCode,
		findAssociatedSubjobs: WorkOrderActions.findAssociatedSubjobs,
		findJWSWorkRequestIds: JobWorkSummaryActions.findJWSWorkRequestIds,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

const enhance = compose<React.ComponentType<OwnProps>>(
	connector,
	withTaskRecovery
);

export default enhance(WorkOrderInfoCard);
